import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Settings, ChevronDown, Globe, Star, Heart, Calendar, Gamepad2, Table2, Trophy, TrendingUp, Sun, Moon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useLang, LANGUAGES } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const APP_LOGO_URL = process.env.REACT_APP_LOGO_URL || '/logo192.png';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { lang, switchLang, t, languages } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [reservDropOpen, setReservDropOpen] = useState(false);
  const [eventsDropOpen, setEventsDropOpen] = useState(false);
  const langMenuRef = useRef(null);
  const reservDropRef = useRef(null);
  const eventsDropRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const languageToggle = {
    es: { next: 'en', label: 'English' },
    default: { next: 'es', label: 'Espanol' },
  }[lang] || { next: 'es', label: 'Espanol' };

  const navLinks = [
    { path: '/store', label: t('nav.store') },
    { path: '/collections', label: t('nav.collections') },
    { path: '/cards', label: t('nav.cards') },
  ];

  const isActive = (path) => location.pathname.startsWith(path);
  const isReservActive = location.pathname.startsWith('/reservations');
  const isEventsActive = location.pathname.startsWith('/events') || location.pathname.startsWith('/leaderboard') || location.pathname.startsWith('/torneos');

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  // Cerrar desplegables al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangMenuOpen(false);
      if (reservDropRef.current && !reservDropRef.current.contains(e.target)) setReservDropOpen(false);
      if (eventsDropRef.current && !eventsDropRef.current.contains(e.target)) setEventsDropOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = (languages || LANGUAGES).find(l => l.code === lang) || LANGUAGES[0];

  return (
    <header className="fixed top-0 w-full z-50 border-b border-primary/20 bg-gradient-to-r from-background/95 via-background/90 to-background/95 shadow-[0_12px_40px_-24px_hsl(var(--primary)/0.75)] backdrop-blur-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="group flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo">
            <div className="relative">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-primary/40 to-emerald-500/30 blur-sm opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <img src={APP_LOGO_URL} alt="El Rincón de Camo" className="relative w-10 h-10 rounded-full object-cover ring-2 ring-primary/40 shadow-lg shadow-primary/20 transition-all duration-300 group-hover:ring-primary/70" />
            </div>
            <span className="hidden sm:block text-foreground font-bold text-base tracking-wide leading-none" style={{ fontFamily: 'Cinzel, serif' }}>El Rincón de Camo</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1.5 p-1 rounded-2xl border border-border/70 bg-card/55 shadow-inner shadow-primary/10">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isActive(link.path) ? 'text-primary-foreground bg-primary/90 shadow-md shadow-primary/30' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}
                data-testid={`nav-${link.path.split('/')[1]}`}>{link.label}</Link>
            ))}

            {/* Events + Ranking dropdown */}
            <div className="relative" ref={eventsDropRef}>
              <button
                onClick={() => setEventsDropOpen(o => !o)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isEventsActive ? 'text-primary-foreground bg-primary/90 shadow-md shadow-primary/30' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}
              >
                {t('nav.events')}
                <ChevronDown size={12} className={`transition-transform ${eventsDropOpen ? 'rotate-180' : ''}`} />
              </button>
              {eventsDropOpen && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-card/95 border border-border rounded-2xl shadow-2xl overflow-hidden z-50 backdrop-blur-xl">
                  <Link to="/events" onClick={() => setEventsDropOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-secondary/50 ${location.pathname === '/events' ? 'text-primary bg-primary/15' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Calendar size={15} className="text-[#C9A962]" />
                    <span>{t('nav.events')}</span>
                  </Link>
                  <Link to="/torneos" onClick={() => setEventsDropOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-secondary/50 ${location.pathname.startsWith('/torneos') ? 'text-primary bg-primary/15' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Trophy size={15} className="text-[#C9A962]" />
                    <span>{t('nav.tournaments')}</span>
                  </Link>
                </div>
              )}
            </div>
            {/* Reservations dropdown */}
            <div className="relative" ref={reservDropRef}>
              <button
                onClick={() => setReservDropOpen(o => !o)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${isReservActive ? 'text-primary-foreground bg-primary/90 shadow-md shadow-primary/30' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}
                data-testid="nav-reservations"
              >
                <Calendar size={14} />
                {t('nav.reservations')}
                <ChevronDown size={12} className={`transition-transform ${reservDropOpen ? 'rotate-180' : ''}`} />
              </button>
              {reservDropOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-card/95 border border-border rounded-2xl shadow-2xl overflow-hidden z-50 backdrop-blur-xl">
                  <Link
                    to="/reservations/games"
                    onClick={() => setReservDropOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-secondary/50 ${location.pathname === '/reservations/games' ? 'text-primary bg-primary/15' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <Gamepad2 size={15} className="text-[#C9A962]" />
                    <span>{t('footer.reserve_game')}</span>
                  </Link>
                  <Link
                    to="/reservations/tables"
                    onClick={() => setReservDropOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-secondary/50 ${location.pathname === '/reservations/tables' ? 'text-primary bg-primary/15' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <Table2 size={15} className="text-[#C9A962]" />
                    <span>{t('footer.reserve_table')}</span>
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Language Dropdown */}
            <div className="relative" ref={langMenuRef}>
              <button 
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-border bg-card/55 transition-all"
                data-testid="lang-toggle"
              >
                <Globe size={14} />
                <span>{currentLang.flag}</span>
                <span className="hidden md:inline">{currentLang.code.toUpperCase()}</span>
                <ChevronDown size={12} className={`transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-card/95 border border-border rounded-2xl shadow-2xl overflow-hidden z-50 backdrop-blur-xl">
                  {(languages || LANGUAGES).map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { switchLang(l.code); setLangMenuOpen(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${lang === l.code ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-secondary/40 hover:text-foreground'}`}
                      data-testid={`lang-${l.code}`}
                    >
                      <span className="text-lg">{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart */}
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-transparent hover:border-border transition-all"
              title={theme === 'dark' ? t('common.light_mode') : t('common.dark_mode')}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <Link to="/cart" className="relative p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-transparent hover:border-border transition-all" data-testid="cart-btn">
              <ShoppingCart size={20} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-br from-primary to-amber-400 text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center shadow-md shadow-primary/40" data-testid="cart-count">{count}</span>
              )}
            </Link>

            {/* User menu */}
            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-secondary/50 border border-border bg-card/45 transition-all text-sm text-foreground"
                  data-testid="user-menu-btn">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0D5E4A] to-[#C9A962] flex items-center justify-center text-xs font-bold text-[#0A1612]">
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden sm:block max-w-[100px] truncate">{user.name}</span>
                  <ChevronDown size={14} className="text-muted-foreground" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-card/95 border border-border rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl" data-testid="user-dropdown">
                    <Link to="/profile" className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors" onClick={() => setUserMenuOpen(false)} data-testid="profile-link">
                      <User size={16} />{t('nav.profile')}
                    </Link>
                    <Link to="/loyalty" className="flex items-center gap-2 px-4 py-3 text-sm text-[#C9A962] hover:text-[#D4B87A] hover:bg-[#C9A962]/10 transition-colors" onClick={() => setUserMenuOpen(false)} data-testid="loyalty-link">
                      <Star size={16} />{t('loyalty.title')}
                    </Link>
                    <Link to="/wishlist" className="flex items-center gap-2 px-4 py-3 text-sm text-[#F87171] hover:text-[#FCA5A5] hover:bg-[#C45B4A]/10 transition-colors" onClick={() => setUserMenuOpen(false)} data-testid="wishlist-link">
                      <Heart size={16} />{t('wishlist.title')}
                    </Link>
                    {(user.role === 'admin' || user.role === 'staff') && (
                      <Link to="/admin" className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors" onClick={() => setUserMenuOpen(false)} data-testid="admin-link">
                        <Settings size={16} />
                        {user.role === 'staff'
                          ? (user.role_name || t('nav.admin'))
                          : t('nav.admin')}
                      </Link>
                    )}
                    <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#C45B4A] hover:bg-[#C45B4A]/10 transition-colors" data-testid="logout-btn">
                      <LogOut size={16} />{t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/auth" className="text-sm px-4 py-2 rounded-xl font-semibold text-primary-foreground bg-gradient-to-r from-primary via-primary to-amber-500 shadow-lg shadow-primary/30 hover:brightness-110 transition-all" data-testid="login-btn">{t('nav.login')}</Link>
            )}

            {/* Mobile menu toggle */}
            <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2.5 rounded-xl border border-border bg-card/50 text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-all" data-testid="mobile-menu-btn">
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden mt-1 bg-card/95 border border-border rounded-2xl py-3 px-1 space-y-1 shadow-2xl backdrop-blur-xl" data-testid="mobile-menu">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path}
                className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setMenuOpen(false)}>{link.label}</Link>
            ))}
            {/* Events + Ranking submenu in mobile */}
            <div className="px-4 py-1">
              <p className="text-muted-foreground text-xs uppercase tracking-wider font-semibold mb-1">{t('nav.events')}</p>
              <Link to="/events" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground">
                <Calendar size={14} className="text-[#C9A962]" />{t('nav.events')}
              </Link>
              <Link to="/torneos" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground">
                <Trophy size={14} className="text-[#C9A962]" />{t('nav.tournaments')}
              </Link>
              <Link to="/torneos?view=ranking" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground">
                <TrendingUp size={14} className="text-[#C9A962]" />{t('nav.ranking')}
              </Link>
            </div>
            <div className="px-4 py-1">
              <p className="text-muted-foreground text-xs uppercase tracking-wider font-semibold mb-1">{t('nav.reservations')}</p>
              <Link to="/reservations/games" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground">
                <Gamepad2 size={14} className="text-[#C9A962]" />
                {t('footer.reserve_game')}
              </Link>
              <Link to="/reservations/tables" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground">
                <Table2 size={14} className="text-[#C9A962]" />
                {t('footer.reserve_table')}
              </Link>
            </div>
            <div className="px-4 pt-2">
              <button onClick={() => { switchLang(languageToggle.next); setMenuOpen(false); }}
                className="text-xs font-semibold text-muted-foreground border border-border px-3 py-1 rounded-md">
                {languageToggle.label}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
